import Button, { ButtonTypes } from "atoms/buttons/button";
import ChatHistorySearch from "./chat-history-search";
import ChatPastConversationsGroup from "organisms/modals/chat-modal/chat-past-conversations-group";
import ConversationRecord from "models/view-models/conversations/conversation-record";
import Icon from "atoms/icons/icon";
import React, { useCallback, useMemo } from "react";
import moment from "moment";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { StringUtils } from "@rsm-hcd/javascript-core";
import { useLocalization } from "utilities/hooks/use-localization";
import { useConversationHistory } from "utilities/atoms/conversations/conversation-history/use-conversation-history.hook";
import { useConversation } from "utilities/atoms/conversations/current-conversation/use-conversation.hook";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import ChatOfflineTooltipContent from "organisms/modals/chat-modal/chat-offline-tooltip-content";
import { ConversationStateStatus } from "utilities/atoms/conversations/current-conversation/conversation-atom.reducer";
import Image from "atoms/images/image";
import CasiLogo from "assets/images/casi-logo.png";
import useModalActions from "utilities/hooks/use-modal-actions";
import AboutCasiModal from "./about-casi-modal";
import { CollectionUtils } from "utilities/collection-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ChatPastConversationsPanelProps {
    closePanel?: () => void;
    handleNewChatStarted?: (value: boolean) => void;
    handleSearchText: React.Dispatch<React.SetStateAction<string>>;
    handleShowSearch: React.Dispatch<React.SetStateAction<boolean>>;
    isOpen?: boolean;
    searchText: string;
    showSearch: boolean;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

export const PAST_CONVO_BASE_CLASS_NAME =
    "c-chat-modal-past-conversations-panel";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatPastConversationsPanel: React.FC<ChatPastConversationsPanelProps> = ({
    closePanel,
    handleNewChatStarted,
    handleSearchText,
    handleShowSearch,
    isOpen,
    searchText,
    showSearch,
}) => {
    const toggleOpen = `${isOpen ? " -open" : " -closed"}`;
    const { t } = useLocalization();
    const { handleOpen, handleClose, isOpen: isVisible } = useModalActions();

    const {
        currentConversation,
        status,
        loadConversation,
        clearConversation,
        cancelPrompt,
        startConversation,
    } = useConversation();
    const { inMobileView } = useBreakpointRanges();
    const { isOnline } = useNetworkInformation();

    const {
        deleteConversation,
        conversations,
        refreshConversationHistory,
        renameConversation,
        pinOrUnpinConversation,
    } = useConversationHistory();

    const handleStartNewChat = () => {
        if (!isOnline) {
            return;
        }

        if (inMobileView) {
            closePanel?.();
        }

        if (status === ConversationStateStatus.StreamingMessage) {
            cancelPrompt();
        }

        clearConversation();
        startConversation();
        handleNewChatStarted?.(true);
    };

    const handleLoadConversation = (conversationId?: string) => {
        if (!conversationId) {
            return;
        }

        if (inMobileView) {
            closePanel?.();
        }

        loadConversation(conversationId);
        refreshConversationHistory();
    };

    const handleRenameConversation = (
        conversation: Pick<ConversationRecord, "id" | "title">
    ) => {
        if (!conversation) {
            return;
        }

        renameConversation(conversation);
    };

    const handleToggleConversationPinned = (
        conversation: Pick<ConversationRecord, "id" | "isPinned">
    ) => {
        if (!conversation) {
            return;
        }

        pinOrUnpinConversation(conversation);
    };

    const formatConversationDate = (date: Date | undefined) => {
        if (!date) {
            return "";
        }
        const today = new Date();
        return moment(date).calendar(today, {
            sameDay: "[Today]",
            lastDay: "[Yesterday]",
            lastWeek: "[Last week]",
            sameElse: "LL",
        });
    };

    const unPinnedGroupedConversationHistory = useMemo(
        () =>
            conversations
                .filter(
                    (conversation) =>
                        StringUtils.hasValue(conversation.title) &&
                        !conversation.isPinned
                )
                .reduce<Record<string, Omit<ConversationRecord, "messages">[]>>(
                    (groups, conversation) => {
                        const dateGroup = formatConversationDate(
                            conversation.createdAt
                        );
                        return {
                            ...groups,
                            [dateGroup]: [
                                ...(groups[dateGroup] ?? []),
                                conversation,
                            ],
                        };
                    },
                    {}
                ),
        [conversations]
    );

    const pinnedConversations = conversations
        .filter((conversation) => conversation.isPinned)
        .sort((a, b) => {
            const aTitle = a.title ?? "";
            const bTitle = b.title ?? "";
            return aTitle.localeCompare(bTitle);
        });

    const handleSearchTriggered = useCallback(
        (searchText: string) => {
            refreshConversationHistory(searchText);
        },
        [refreshConversationHistory]
    );

    return (
        <>
            <div className={`${PAST_CONVO_BASE_CLASS_NAME} ${toggleOpen}`}>
                <Button
                    accessibleText={t("close")}
                    style={
                        isOpen
                            ? ButtonStyles.TertiaryAlt
                            : ButtonStyles.Tertiary
                    }
                    cssClassName={`${PAST_CONVO_BASE_CLASS_NAME}__close`}
                    onClick={closePanel}
                    size={ButtonSizes.Small}>
                    <Icon type={Icons.Menu2} size={IconSizes.Medium} />
                </Button>
                <div
                    className={`${PAST_CONVO_BASE_CLASS_NAME}__container`}
                    data-test-id={"tab-panel"}>
                    <div className={`${PAST_CONVO_BASE_CLASS_NAME}__header`}>
                        <Image
                            altText="CASI logo"
                            src={CasiLogo}
                            cssClassName={`${PAST_CONVO_BASE_CLASS_NAME}__header__logo`}
                        />

                        <Button
                            cssClassName={`${PAST_CONVO_BASE_CLASS_NAME}__header__about`}
                            size={ButtonSizes.Small}
                            type={ButtonTypes.Button}
                            style={ButtonStyles.TertiaryAlt}
                            onClick={() => handleOpen()}>
                            <Icon type={Icons.Information} />
                            {t("sitnav-about-buttonText")}
                        </Button>
                    </div>

                    <div className={`${PAST_CONVO_BASE_CLASS_NAME}__content`}>
                        <div
                            className={`${PAST_CONVO_BASE_CLASS_NAME}__content-buttons`}>
                            <Tooltip
                                disabled={isOnline}
                                content={<ChatOfflineTooltipContent />}
                                durationInMs={0}
                                hideOnClick={true}
                                placement={TooltipPlacement.AutoStart}>
                                <Button
                                    size={ButtonSizes.Small}
                                    cssClassName={`${PAST_CONVO_BASE_CLASS_NAME}__buttons`}
                                    style={ButtonStyles.TertiaryAlt}
                                    onClick={handleStartNewChat}
                                    aria-disabled={!isOnline}>
                                    <Icon
                                        size={IconSizes.Base}
                                        type={Icons.Pen}
                                    />
                                    {t("start-new-chat")}
                                </Button>
                            </Tooltip>
                            <ChatHistorySearch
                                handleSearchText={handleSearchText}
                                searchText={searchText}
                                onShowSearch={handleShowSearch}
                                offline={!isOnline}
                                showSearch={showSearch}
                                onSearchTriggered={handleSearchTriggered}
                            />
                        </div>

                        {CollectionUtils.hasValues(pinnedConversations) && (
                            <ChatPastConversationsGroup
                                isPinnedGroup
                                key={"pinned"}
                                name={t("pinned")}
                                conversations={pinnedConversations}
                                selectedConversationId={currentConversation?.id}
                                onConversationSelected={handleLoadConversation}
                                onConversationRenamed={handleRenameConversation}
                                onConversationDelete={deleteConversation}
                                onConversationPinToggle={
                                    handleToggleConversationPinned
                                }
                            />
                        )}

                        {Object.keys(unPinnedGroupedConversationHistory).map(
                            (groupName) => (
                                <ChatPastConversationsGroup
                                    key={groupName}
                                    name={groupName}
                                    conversations={
                                        unPinnedGroupedConversationHistory[
                                            groupName
                                        ]
                                    }
                                    selectedConversationId={
                                        currentConversation?.id
                                    }
                                    onConversationSelected={
                                        handleLoadConversation
                                    }
                                    onConversationRenamed={
                                        handleRenameConversation
                                    }
                                    onConversationDelete={deleteConversation}
                                    onConversationPinToggle={
                                        handleToggleConversationPinned
                                    }
                                />
                            )
                        )}
                    </div>
                </div>
            </div>

            <AboutCasiModal isVisible={isVisible} closeDialog={handleClose} />
        </>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChatPastConversationsPanel;

// #endregion Exports
